import { type Draft, type Objectish } from "immer";
import {
	type FC,
	type PropsWithChildren,
	createContext,
	useCallback,
	useContext,
	useMemo,
} from "react";

import { type Store } from "@moment/api-collab/differential";
import { type DatabaseTables } from "@moment/api-collab/differential/types";

export type DoTransactionFn<TState extends Objectish> = (draft: Draft<TState>) => void;

export interface CellServicesContextState {
	cellId: string;
	cellName: string;
	currentUser: { id: string; name: string; profilePicture: string } | null;
	users: { id: string; name: string; profilePicture: string }[];
	pageStorage: Objectish;
	pageStorageCommit: (doTransaction: DoTransactionFn<Objectish>) => Promise<void>;
	documentInteractionMode: "static" | "interactive";
	base: Store<DatabaseTables> | null;
}

const CellServicesContext = createContext<CellServicesContextState>({
	cellId: "",
	cellName: "",
	users: [],
	pageStorage: {},
	pageStorageCommit: () => Promise.resolve(),
	documentInteractionMode: "interactive",
	currentUser: null,
	base: null,
});

export const useCellServices = () => {
	const context = useContext(CellServicesContext);
	return context;
};

export const CellServicesContextProvider: FC<
	PropsWithChildren & {
		cellId: string;
		cellName: string;
		pageStorage: Objectish;
		pageStorageCommit: (doTransaction: DoTransactionFn<Objectish>) => Promise<void>;
		documentInteractionMode: "static" | "interactive";
		currentUser: { id: string; name: string; profilePicture: string } | null;
		base: Store<DatabaseTables>;
	}
> = ({
	children,
	cellId,
	cellName,
	pageStorage,
	pageStorageCommit: _pageStorageCommit,
	documentInteractionMode,
	currentUser,
	base,
}) => {
	// `pageStorageCommit` is always a normal, non-Reactified function.
	const pageStorageCommit = useCallback(_pageStorageCommit, []);

	const value: CellServicesContextState = useMemo(() => {
		return {
			cellId,
			cellName,
			users: currentUser ? [currentUser] : [],
			pageStorage,
			pageStorageCommit,
			// documentInteractionMode: "static",
			documentInteractionMode,
			currentUser,
			base,
		};
	}, [
		cellId,
		cellName,
		currentUser,
		pageStorage,
		pageStorageCommit,
		documentInteractionMode,
		base,
	]);
	return <CellServicesContext.Provider value={value}>{children}</CellServicesContext.Provider>;
};

export const useCellState = (): [
	Objectish,
	(doTransaction: DoTransactionFn<Objectish>) => Promise<void>,
] => {
	const { cellId, pageStorage, pageStorageCommit } = useCellServices();

	const setCellState = useCallback(
		(doTransaction: DoTransactionFn<Objectish>) =>
			pageStorageCommit((store) => {
				// @ts-ignore - store[cellId] is not typed
				store[cellId] = doTransaction(store[cellId] ?? {});
			}),
		[cellId, pageStorageCommit]
	);

	return useMemo(
		// @ts-ignore - pageStorage is not typed
		() => [pageStorage?.[cellId], setCellState],
		[cellId, pageStorage, setCellState]
	);
};
